import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Rocket } from "lucide-react";
import logo from "@/assets/angkasa-logo.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const stars = useMemo(() => Array.from({ length: 60 }, () => ({
    top: Math.random() * 100, left: Math.random() * 100,
    size: Math.random() * 2 + 1, delay: Math.random() * 4, dur: Math.random() * 3 + 2,
  })), []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal, periksa email & password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row font-poppins relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#0a0e2a 0%,#141a4a 45%,#2a1a5e 100%)" }}>
      {/* Stars */}
      {stars.map((s, i) => (
        <span key={i} className="absolute rounded-full bg-white" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: s.size, height: s.size,
          animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
        }} />
      ))}
      {/* Planets & rocket */}
      <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full animate-drift" style={{ background: "radial-gradient(circle at 30% 30%,#7c3aed,#3b1d8f)", filter: "blur(2px)", opacity: 0.7 }} />
      <div className="absolute bottom-10 -left-10 w-40 h-40 rounded-full animate-drift" style={{ background: "radial-gradient(circle at 40% 40%,#38bdf8,#0369a1)", opacity: 0.6, animationDelay: "4s" }} />
      <Rocket className="absolute bottom-24 right-24 w-10 h-10 text-sky-300/80 animate-rocket hidden sm:block" />

      {/* Brand */}
      <div className="lg:flex-1 relative z-10 flex items-center justify-center px-8 py-10">
        <div className="text-center lg:text-left max-w-md animate-fade-in-up">
          <div className="w-24 h-24 mx-auto lg:mx-0 rounded-3xl bg-white/10 backdrop-blur-md flex items-center justify-center p-1.5 mb-6 overflow-hidden ring-1 ring-white/30"
            style={{ boxShadow: "0 0 50px rgba(124,58,237,0.7), 0 0 90px rgba(56,189,248,0.4)" }}>
            <img src={logo} alt="ANGKASA STORE" className="w-full h-full object-cover rounded-2xl" />
          </div>
          <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight tracking-tight" style={{ textShadow: "0 0 30px rgba(56,189,248,0.6)" }}>ANGKASA<br className="hidden lg:block" /> STORE</h1>
          <p className="mt-4 text-sky-200/80 text-base">Luxury Space Management — kasir, service, inventaris & keuangan untuk cabang Tegal & Brebes.</p>
        </div>
      </div>

      {/* Form (glassmorphism) */}
      <div className="lg:flex-1 relative z-10 flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} data-testid="login-form"
          className="w-full max-w-sm rounded-3xl p-8 animate-fade-in-up bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl"
          style={{ animationDelay: "0.15s" }}>
          <h2 className="text-2xl font-semibold text-white">Selamat Datang 🚀</h2>
          <p className="text-sm text-sky-200/70 mb-6 mt-1">Masuk untuk mengelola toko Anda</p>
          <div className="space-y-4">
            <div>
              <Label className="text-sky-100 text-sm">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com"
                className="mt-1.5 rounded-xl h-11 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:border-sky-400" data-testid="login-email" required />
            </div>
            <div>
              <Label className="text-sky-100 text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="rounded-xl h-11 pr-11 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:border-sky-400" data-testid="login-password" required />
                <button type="button" onClick={() => setShow((v) => !v)} data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-sky-300">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 rounded-xl font-medium text-base transition-transform duration-150 hover:scale-[1.03] active:scale-[0.97]"
              style={{ background: "linear-gradient(90deg,#7c3aed,#38bdf8)", boxShadow: "0 10px 30px rgba(56,189,248,0.4)" }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Masuk"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
